/* Every word in the admin portal.
 *
 * ITS OWN TABLE, NOT THE APPS' ONE, for the reason the dashboard's own file
 * gives: the Arabic sheet is scoped to the customer app and the till (ruled
 * 2026-09-17), and everything else comes after those two. English only, `ar`
 * null on every entry, and `scripts/copy-check.mjs` still reads this file for
 * the house rules: no em-dashes, no words ruled off every surface, and no
 * string that guesses somebody's gender.
 *
 * THIRTEEN OF THE TWENTY-ONE SCREENS ARE EMPTY STATES, and their words are the
 * most carefully written in the file. An empty screen has one job: say what is
 * not there, so that nobody plans around a number that does not exist. None of
 * them says "coming soon", because none of them is promised.
 *
 * WHAT IS NOT IN HERE: no riyal figure, no plan, no MRR, no uptime percentage,
 * no impersonation, and no consumer lookup. See reports/surfaces/admin-portal.md
 * for the reading that cut each one, and why the last two are cut rather than
 * shelled.
 */
export type Lang = "en" | "ar";

type Entry = { en: string; ar: string | null; review: null; source: null };

const S = (en: string): Entry => ({ en, ar: null, review: null, source: null });

export const COPY = {
  // --- the shell -----------------------------------------------------------
  "shell.title": S("Damga admin"),
  "shell.signOut": S("Sign out"),
  "shell.menu": S("Menu"),
  "shell.menuClose": S("Close menu"),
  "shell.loading": S("Loading"),
  "shell.offline": S("No connection. Nothing here is up to date."),
  "shell.problem": S("Something went wrong reading the platform. Nothing was changed."),
  // CORRECTED 2026-09-18, found by the UI review: this said "Read only. Nothing on
  // this screen changes anything." on every screen, and it stopped being true the
  // day the portal began issuing merchant sign-ins. It now names the two writes.
  "shell.readOnly": S("Two things here change anything: setting up a shop, and sending an owner a new link. Everything else only reads."),

  // --- the sidebar ---------------------------------------------------------
  "group.platform": S("Platform"),
  "group.merchants": S("Merchants"),
  "group.consumers": S("Consumers"),
  "group.trust": S("Trust and safety"),
  "group.operations": S("Operations"),

  "nav.overview": S("Overview"),
  "nav.revenue": S("Revenue and billing"),
  "nav.finance": S("Finance"),
  "nav.health": S("System health"),
  "nav.infra": S("Infrastructure"),
  "nav.settings": S("Platform settings"),
  "nav.merchants": S("Merchants"),
  "nav.merchant": S("One merchant"),
  "nav.pipeline": S("Onboarding pipeline"),
  "nav.provision": S("Set up a shop"),
  "nav.consumers": S("Consumer accounts"),
  "nav.consumer": S("One consumer"),
  "nav.app": S("Consumer app"),
  "nav.fraud": S("Flagged stamps"),
  "nav.compliance": S("Compliance and privacy"),
  "nav.content": S("Content and reviews"),
  "nav.audit": S("Audit log"),
  "nav.support": S("Support"),
  "nav.messaging": S("Messaging"),
  "nav.integrations": S("Integrations and API"),
  "nav.flags": S("Feature flags"),
  "nav.staff": S("Staff and roles"),
  "nav.outbox": S("Outbox"),

  // --- sign in -------------------------------------------------------------
  "auth.title": S("Damga admin"),
  "auth.body": S("For Damga's own staff. Sign in with your Damga address."),
  "auth.google": S("Continue with Google"),
  "auth.microsoft": S("Continue with Microsoft"),
  "auth.email": S("Continue with email"),
  "auth.emailLabel": S("Damga email address"),
  "auth.send": S("Send the code"),
  "auth.codeLabel": S("Six digit code"),
  "auth.verify": S("Sign in"),
  "auth.changeEmail": S("Use a different address"),
  "auth.badEmail": S("That does not look like an email address."),
  "auth.badCode": S("That code is not right. Check it and try again."),
  "auth.tooMany": S("Too many attempts. Wait a minute and try again."),
  "auth.unreachable": S("Damga is unreachable from here. Nothing was sent."),
  "auth.notStaff": S("This account is not on Damga's staff list."),
  "auth.notStaffBody": S(
    "The list is a deployment setting, not something anybody can grant from this screen. " +
      "If you should be on it, ask whoever runs the deployment to add your address.",
  ),

  // --- overview ------------------------------------------------------------
  "overview.title": S("Platform overview"),
  "overview.lead": S("Every figure here is a row the database can produce today."),
  "overview.group.size": S("How much of Damga exists"),
  "overview.group.happened": S("What has happened on it"),
  "overview.group.waiting": S("Waiting for a person"),
  "overview.businesses": S("Shops"),
  "overview.trading": S("Shops that have stamped"),
  "overview.memberships": S("Cards"),
  "overview.people": S("People with an account"),
  "overview.stamps": S("Stamps, all time"),
  "overview.stamps30": S("Stamps, last 30 days"),
  "overview.rewards": S("Rewards taken"),
  "overview.leads": S("Merchants who asked us to call"),
  "overview.winding": S("Shops winding down"),
  "overview.erasures": S("Erasures honoured"),
  "overview.noMoney": S(
    "There is no revenue figure on this screen because nothing in Damga charges anybody yet. " +
      "The pilot is free for ninety days and no billing exists to report.",
  ),

  // --- merchants -----------------------------------------------------------
  // --- setting up a shop (2026-09-18): Damga issues the merchant's sign-in ------
  "provision.title": S("Set up a shop"),
  "provision.lead": S("A shop, its owner's sign-in and its first till, created together. The owner gets an email with a link to choose their own password. Nobody at Damga sees it, and nobody here can."),
  "provision.name": S("Shop name"),
  "provision.kind": S("Kind of shop"),
  "provision.city": S("City"),
  "provision.number": S("The shop's number"),
  "provision.numberHint": S("+974 and eight digits: the number the shop answers."),
  "provision.username": S("Owner's username"),
  "provision.usernameHint": S("Lowercase letters, digits, dots or dashes, for example nour.cafe. Not secret: the owner signs in with it."),
  "provision.email": S("Owner's email"),
  "provision.submit": S("Set up and send the link"),
  "provision.done": S("{name} is set up. {email} has a link to choose a password; it works once, for three days."),
  "provision.already": S("{name} is set up. The owner already had a Damga sign-in, and it now opens this shop."),
  "provision.problem": S("Not set up: {reason}."),
  "provision.mailDown": S("Not set up: the email could not be sent just now, so nothing was created. Try again in a minute."),
  "provision.next": S("The owner starts their card and adds the people on their till from the dashboard."),

  "merchants.ownerLink": S("Send the owner a new link"),
  "merchants.ownerLinkSent": S("Sent. The link before it no longer works."),
  "merchants.ownerLinkProblem": S("Not sent"),
  "merchants.ownerLinkTooMany": S("Not sent: three links have gone to this owner today. The next can go after {at}."),
  "merchants.ownerLinkMailDown": S("Not sent: the email could not go just now. Try again in a minute."),

  // --- staging's outbox: what the API would have emailed -------------------------
  "outbox.title": S("Outbox"),
  "outbox.lead": S("Staging sends no email. Everything the API would have sent is here instead, so a tester can open a setup link. Doha has no outbox: the API refuses to start with one."),
  "outbox.empty": S("Nothing sent yet"),
  "outbox.emptyBody": S("Set up a shop, or sign in on a new device, and the email appears here."),
  "outbox.to": S("To"),

  "merchants.title": S("Merchants"),
  "merchants.lead": S("Every shop, and what it has actually done."),
  "merchants.search": S("Search shops"),
  "merchants.col.name": S("Shop"),
  "merchants.col.category": S("Kind"),
  "merchants.col.number": S("Number"),
  "merchants.noNumber": S("None on file"),
  "merchants.col.joined": S("Joined"),
  "merchants.col.members": S("Cards"),
  "merchants.col.stamps": S("Stamps"),
  "merchants.col.staff": S("Staff"),
  "merchants.col.last": S("Last stamp"),
  "merchants.never": S("Never"),
  "merchants.winding": S("Winding down"),
  "merchants.closed": S("Closed"),
  "merchants.count": S("{n} shops"),
  "merchants.empty": S("No shops yet"),
  "merchants.emptyBody": S("The first shop appears here the day somebody is set up on Damga."),
  "merchants.noPlan": S(
    "There is no plan column. Damga has one price, and it is on the public page.",
  ),

  // --- one merchant --------------------------------------------------------
  "merchant.title": S("One merchant"),
  "merchant.empty": S("Nothing here that the list does not already show"),
  "merchant.emptyBody": S(
    "A merchant's own screens are their dashboard, and Damga does not sign in as a merchant. " +
      "Everything the platform knows about a shop without opening their account is in the list.",
  ),

  // --- pipeline ------------------------------------------------------------
  "pipeline.title": S("Onboarding pipeline"),
  "pipeline.lead": S(
    "Merchants who asked us to call, newest first. Opening this screen is written to the audit log.",
  ),
  "pipeline.col.asked": S("Asked"),
  "pipeline.col.name": S("Who"),
  "pipeline.col.business": S("Shop"),
  "pipeline.col.contact": S("Contact"),
  "pipeline.col.consent": S("Consent"),
  "pipeline.col.doorbell": S("Doorbell"),
  "pipeline.consented": S("Given"),
  "pipeline.notConsented": S("Not recorded"),
  "pipeline.askedAgain": S("Asked again {at}"),
  "pipeline.empty": S("Nobody has asked yet"),
  "pipeline.emptyBody": S("The form on damga.qa writes here, and the doorbell email says only that somebody did."),
  "pipeline.noStages": S(
    "There are no stages, documents or verification steps. A lead is somebody who asked, " +
      "and the next step is a person picking up the phone.",
  ),

  // --- compliance ----------------------------------------------------------
  "compliance.title": S("Compliance and privacy"),
  "compliance.lead": S("Dates and counts. The portal never shows who was erased."),
  "compliance.erasures": S("Erasures honoured"),
  "compliance.erasuresBody": S(
    "The register keeps a reference and a date so the right survives a restore. " +
      "The reference is not read by this screen and does not leave the database.",
  ),
  "compliance.day": S("Day"),
  "compliance.count": S("Erasures"),
  "compliance.noErasures": S("Nobody has asked to be erased"),
  "compliance.versions": S("Consent wording in force"),
  "compliance.col.channel": S("Where"),
  "compliance.col.locale": S("Language"),
  "compliance.col.state": S("State"),
  "compliance.col.notice": S("Privacy notice"),
  "compliance.col.approved": S("Approved by"),
  "compliance.notApproved": S("Not approved"),
  "compliance.channel.enrolment": S("At the counter"),
  "compliance.channel.interest": S("On the website"),
  "compliance.noVersions": S("No wording recorded"),
  "compliance.noRequests": S(
    "There is no data request queue. A request arrives by email and is answered by a person; " +
      "an erasure shows up here once it has been done.",
  ),

  // --- audit ---------------------------------------------------------------
  "audit.title": S("Audit log"),
  "audit.lead": S(
    "What the product records about Damga's own people: every shop set up, every new sign-in " +
      "link sent, and every look at the merchants who asked us to call.",
  ),
  "audit.did": S("What Damga did"),
  "audit.looked": S("Who looked at the leads"),
  "audit.col.what": S("What"),
  "audit.did.none": S("Nobody at Damga has set up a shop or sent a link yet. The first line appears the moment somebody does, with who and when."),
  "audit.looked.none": S("Nobody has opened the leads yet. The first line appears the moment somebody does, including you."),
  "audit.action.provision_shop": S("Set up {shop}. Its owner signs in as {username}."),
  "audit.action.owner_link": S("Sent the owner of {shop} ({username}) a new sign-in link. The one before it stopped working."),
  "audit.action.unknown": S("Did something this screen has no words for yet."),
  "audit.gone.shop": S("a shop no longer on file"),
  "audit.gone.account": S("an account no longer on file"),
  "audit.col.at": S("When"),
  "audit.col.viewer": S("Damga account"),
  "audit.col.shown": S("Rows shown"),
  "audit.narrow": S(
    "This log covers three things: a shop set up, an owner sent a new link, and a look at the " +
      "leads. It never holds a link itself. It is not a record of every action on the platform, " +
      "and it does not carry addresses or devices.",
  ),

  // --- flagged stamps ------------------------------------------------------
  "fraud.title": S("Flagged stamps"),
  "fraud.lead": S(
    "Stamps the counter marked for a human eye. Every one of them still counted.",
  ),
  // IN WORDS, NOT CODES (ruled 2026-09-18: "a code on a screen is a defect"). One
  // sentence for each reason the API writes (ledger_entries.flagged_reason, 027).
  "fraud.reason.same_pair": S(
    "The same person at the till stamped the same customer again within a few hours, more often than the shop's limit.",
  ),
  "fraud.reason.till_velocity": S("This till stamped more cards in an hour than a till normally does."),
  "fraud.reason.device_clock": S(
    "The till's clock was ahead of the real time, so the stamp is dated when it reached Damga.",
  ),
  "fraud.reason.unknown": S("Flagged for a reason this screen has no words for yet."),
  "fraud.col.at": S("When"),
  "fraud.col.business": S("Shop"),
  "fraud.col.reason": S("Why it was flagged"),
  "fraud.empty": S("Nothing has been flagged"),
  "fraud.emptyBody": S(
    "A stamp is flagged when the same person stamps the same card too often, a till stamps unusually fast, or a till's clock is wrong.",
  ),
  "fraud.noRules": S(
    "There is no rules engine, no blocked device list and no case file. A flag is a note on " +
      "one stamp, and the stamp still counted.",
  ),

  // --- infrastructure ------------------------------------------------------
  "infra.title": S("Infrastructure"),
  "infra.lead": S("Where Damga runs, stated rather than measured."),
  "infra.region": S("Region"),
  "infra.regionValue": S("Doha, Qatar. One region."),
  "infra.regionBody": S(
    "There is no replica and no backup outside Qatar. Real merchant and customer data " +
      "never leaves the country, which is why there is nothing else to list here.",
  ),
  "infra.backups": S("Backups"),
  "infra.backupsValue": S("Kept 30 days"),
  "infra.backupsBody": S("The same 30 days the privacy notice on damga.qa states."),
  "infra.noUptime": S(
    "There are no uptime figures on this screen because nothing measures uptime yet. " +
      "A number here would be a number somebody typed.",
  ),

  // --- staff ---------------------------------------------------------------
  "staff.title": S("Staff and roles"),
  "staff.lead": S("Who can open this portal, and how that is decided."),
  "staff.how": S("An allowlist of email addresses"),
  "staff.howBody": S(
    "Access is a list of addresses in the deployment's settings. An address on the list can " +
      "read every screen here; an address that is not on it is refused. There is nothing to " +
      "invite, promote or revoke from this screen.",
  ),
  "staff.oneLevel": S("One level of access, not five"),
  "staff.oneLevelBody": S(
    "There are no roles, because there is nothing for a role to separate: every route this " +
      "portal reads is a read, and it shows the same thing to everybody on the list.",
  ),
  "staff.failsClosed": S("An empty list refuses everyone"),
  "staff.failsClosedBody": S(
    "A deployment that nobody configured shows nobody anything, which is the safe direction " +
      "for the setting to fail in.",
  ),

  // --- the screens with nothing behind them --------------------------------
  "empty.what": S("Not built"),
  "revenue.title": S("Revenue and billing"),
  "revenue.empty": S("Nothing charges anybody yet"),
  "revenue.emptyBody": S(
    "The pilot is free for ninety days, and no invoice, payment or subscription exists in " +
      "Damga. When one does, it will be a real figure on this screen rather than a sample.",
  ),
  "finance.title": S("Finance"),
  "finance.empty": S("No money moves through Damga"),
  "finance.emptyBody": S(
    "Rewards earned and not yet taken are a real obligation, and the dashboard shows each " +
      "shop its own. They are counted in stamps, because no amount is captured anywhere.",
  ),
  "health.title": S("System health"),
  "health.empty": S("Nothing is measured yet"),
  "health.emptyBody": S(
    "There is no uptime probe, error budget or status page. The honest answer to whether " +
      "Damga is up is to open it.",
  ),
  "settings.title": S("Platform settings"),
  "settings.empty": S("Settings are deployment settings"),
  "settings.emptyBody": S(
    "What this screen would change lives in the deployment: the staff list, the region, the " +
      "keys. None of it is a switch, and a switch here would be a second place to get it wrong.",
  ),
  "consumers.title": S("Consumer accounts"),
  "consumers.empty": S("Damga does not browse its customers"),
  "consumersDetail.title": S("One consumer"),
  "consumers.emptyBody": S(
    "A list of people, their numbers and the shops they visit is the thing this portal must " +
      "not have. Nobody at Damga needs it to run the platform, and a right to see somebody's " +
      "record is exercised through a request, with a record of who asked and why. " +
      "The counts on Overview are what the platform needs.",
  ),
  "app.title": S("Consumer app"),
  "app.empty": S("The app is deployed from a commit"),
  "app.emptyBody": S(
    "There is no release channel, adoption figure, crash report or store rating. The app is " +
      "a web app, and what is live is whatever commit was last deployed.",
  ),
  "content.title": S("Content and reviews"),
  "content.empty": S("Damga has no reviews"),
  "content.emptyBody": S(
    "Nothing in the product lets a customer write about a shop, so there is nothing to " +
      "moderate and no policy to apply.",
  ),
  "flags.title": S("Feature flags"),
  "flags.empty": S("There is no flag system"),
  "flags.emptyBody": S(
    "A change goes out by deploying it. Nothing can be turned on for a quarter of the shops.",
  ),
  "support.title": S("Support"),
  "support.empty": S("Support is an inbox"),
  "support.emptyBody": S(
    "There is no ticket queue. There is also no way for Damga to sign in as a merchant, " +
      "deliberately: an account that can read every shop's customers is the thing the " +
      "product is built to make impossible.",
  ),
  "messaging.title": S("Messaging"),
  "messaging.empty": S("Damga sends one email"),
  "messaging.emptyBody": S(
    "A merchant asking us to call rings a doorbell that says only that somebody asked, and " +
      "when. No name or number travels through the mail provider. There is no other channel.",
  ),
  "integrations.title": S("Integrations and API"),
  "integrations.empty": S("There is no public API"),
  "integrations.emptyBody": S(
    "No partner keys, no webhooks and nothing to rate limit. The counter and the apps talk " +
      "to Damga's own API with the signed in person's session.",
  ),
} as const satisfies Record<string, Entry>;

export type Key = keyof typeof COPY;
export type CopyKey = Key;

/** English, with {placeholders} filled. */
export function t(key: Key, vars?: Record<string, string | number>): string {
  const s = COPY[key].en;
  if (!vars) return s;
  return s.replace(/\{(\w+)\}/g, (whole, name: string) =>
    name in vars ? String(vars[name]) : whole,
  );
}

/** Every string here is the author's; none has been read by the founder. */
export const PENDING_APPROVAL: readonly { prefix?: string; keys?: readonly string[]; because: string }[] = [
  {
    prefix: "",
    because:
      "The admin portal, built 2026-09-17. Every line is written here rather than "
      + "mined from the artboard, because the artboard's portal describes revenue, "
      + "impersonation, a consumer lookup, messaging and an API that do not exist.",
  },
];

export function pendingApproval(): Key[] {
  return Object.keys(COPY) as Key[];
}

/** All of them: this surface ships English until the customer app and the till
 *  have Arabic a native speaker has read. */
export function missingArabic(): Key[] {
  return Object.keys(COPY) as Key[];
}
